// Checkout Component - Full checkout flow with payment
import React, { useState, useMemo, useEffect } from 'react';
import { api } from '../api/client';
import { LoyaltyPanel } from './LoyaltyPanel';
import {
  isApplePayAvailable,
  isGooglePayAvailable,
  startApplePay,
  startGooglePay,
} from '../api/wallet';

// A scheduled order time is picked as a WALL-CLOCK time in the VENUE's timezone
// (e.g. 11:30 AM at a Central-time venue), regardless of where the customer's
// browser is. The POS reads pickup_datetime as UTC and converts back to venue
// local for display, so we must store UTC here — otherwise "11:30" is treated
// as UTC and shown 5–6h early. This converts a venue-local 'YYYY-MM-DDTHH:mm:ss'
// to the correct UTC instant, DST-safe, using only Intl (no library).
//
// Returns a Date (the UTC instant). Falls back to interpreting the string in
// the browser's local zone if tz is missing/invalid.
function venueLocalToUTC(localStr, tz) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localStr || '');
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m.map(Number);
  if (!tz) return new Date(y, mo - 1, d, h, mi, s || 0); // browser-local fallback

  // Offset (ms) between the venue wall clock and UTC at a given instant:
  // formats the instant in `tz`, reads back the wall-clock numbers, and compares
  // them to the same numbers interpreted as UTC. offset = venueLocal - utc.
  const offsetAt = (instant) => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).formatToParts(instant);
      const p = {};
      for (const part of parts) p[part.type] = part.value;
      let hh = parseInt(p.hour, 10);
      if (hh === 24) hh = 0; // some engines emit '24' for midnight
      const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, hh, +p.minute, +p.second);
      return asUTC - instant.getTime();
    } catch (e) { return 0; }
  };

  // First guess: pretend the wall clock is UTC, then correct by the offset at
  // that instant. One refinement pass resolves DST-boundary edge cases.
  const guess = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  let utc = guess - offsetAt(new Date(guess));
  utc = guess - offsetAt(new Date(utc));
  return new Date(utc);
}

// Format a Date as a UTC 'YYYY-MM-DDTHH:mm:ss' string (no trailing Z) — matches
// the shape the backend already accepts for pickup_datetime.
function toUTCStamp(date) {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${p(date.getUTCMonth() + 1)}-${p(date.getUTCDate())}` +
         `T${p(date.getUTCHours())}:${p(date.getUTCMinutes())}:${p(date.getUTCSeconds())}`;
}

// --- Schedule / hours helpers ---------------------------------------------
const SCHED_DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Map a card-network name (from a wallet or the processor) to the single-letter
// code used elsewhere in the app: V/M/A/D, or 'C' for anything unknown. Never
// maps the wallet sentinels ('AP'/'GP') to a real network.
function cardCodeFromNetwork(net) {
  const s = String(net || '').toUpperCase().trim();
  if (!s || s === 'AP' || s === 'GP' || s === 'APPLEPAY' || s === 'GOOGLEPAY') return 'C';
  if (s.indexOf('VISA') !== -1 || s === 'V') return 'V';
  if (s.indexOf('MASTER') !== -1 || s.indexOf('MAST') !== -1 || s === 'MC' || s === 'M') return 'M';
  if (s.indexOf('AMEX') !== -1 || s.indexOf('AMER') !== -1 || s.indexOf('AMERICAN') !== -1 || s === 'A') return 'A';
  if (s.indexOf('DISC') !== -1 || s === 'D') return 'D';
  return 'C';
}

// The active schedule for scheduling: business hours when useVenueHours is on,
// else online hours. Falls back to whichever exists.
function activeSchedule(settings) {
  const hc = (settings && settings.hoursConfig) || {};
  const chosen = hc.useVenueHours ? hc.businessHours : hc.onlineHours;
  return (chosen && typeof chosen === 'object') ? chosen : (hc.onlineHours || hc.businessHours || null);
}

// Normalize one day's schedule into an array of {open, close} HHMM windows,
// supporting split days. Same shapes as the App-side dayWindows.
function schedDayWindows(daySchedule) {
  if (!daySchedule) return [];
  const push = (arr, o, c) => {
    const open = parseInt(o, 10), close = parseInt(c, 10);
    if (!Number.isNaN(open) && !Number.isNaN(close)) arr.push({ open, close });
  };
  if (Array.isArray(daySchedule)) {
    const out = [];
    daySchedule.forEach(w => { if (w && w.enabled !== false) push(out, w.open, w.close); });
    return out.sort((a, b) => a.open - b.open);
  }
  if (typeof daySchedule === 'object') {
    if (daySchedule.enabled === false) return [];
    if (Array.isArray(daySchedule.windows)) {
      const out = [];
      daySchedule.windows.forEach(w => { if (w && w.enabled !== false) push(out, w.open, w.close); });
      return out.sort((a, b) => a.open - b.open);
    }
    const out = [];
    push(out, daySchedule.open, daySchedule.close);
    push(out, daySchedule.open2, daySchedule.close2);
    push(out, daySchedule.open3, daySchedule.close3);
    return out.sort((a, b) => a.open - b.open);
  }
  return [];
}

function schedFormatHHMM(hhmm) {
  const h = Math.floor(hhmm / 100), m = hhmm % 100;
  const ampm = h >= 12 ? 'PM' : 'AM';
  let hr = h % 12; if (hr === 0) hr = 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
}

const hhmmToMin = (hhmm) => Math.floor(hhmm / 100) * 60 + (hhmm % 100);
const minToHHMM = (min) => Math.floor(min / 60) * 100 + (min % 60);

// Day-of-week (0=Sun) for a 'YYYY-MM-DD' date, computed at UTC noon so it never
// shifts due to browser timezone.
function dowForYMD(ymd) {
  const [Y, M, D] = (ymd || '').split('-').map(Number);
  if (!Y || !M || !D) return -1;
  return new Date(Date.UTC(Y, M - 1, D, 12)).getUTCDay();
}

// Current venue-local { ymd, hhmm } for "skip past slots" logic.
function venueNowYMDHHMM(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    const p = {};
    for (const part of parts) p[part.type] = part.value;
    let hh = parseInt(p.hour, 10); if (hh === 24) hh = 0;
    return { ymd: `${p.year}-${p.month}-${p.day}`, hhmm: hh * 100 + parseInt(p.minute, 10) };
  } catch (e) {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return { ymd: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, hhmm: d.getHours() * 100 + d.getMinutes() };
  }
}

export function Checkout({
  venueid,
  cart,
  cartTotals,
  customer,
  orderType,
  availableOrderTypes = [],
  deliveryAddress,
  scheduledTime,
  venueOpen = true,
  promoCode,
  settings,
  venueData,
  onBack,
  onCustomerEdit,
  onOrderTypeChange,
  onDeliveryAddressChange,
  onScheduleChange,
  onApplyPromo,
  onOrderComplete
}) {
  const [step, setStep] = useState('details'); // details, payment
  const [tip, setTip] = useState(0);
  const [tipType, setTipType] = useState('percent'); // percent, custom
  const [customTip, setCustomTip] = useState('');
  // Loyalty: applied redemption + active subscription discount (from LoyaltyPanel)
  const [loyaltyRedemption, setLoyaltyRedemption] = useState(null);
  const [loyaltySub, setLoyaltySub] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  
  // Payment state
  // `paymentMethod` now selects the tender for the REMAINING balance after any
  // applied gift cards (card | applepay | googlepay). Gift cards stack on top
  // via `appliedGiftCards` and are charged first at submit time.
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardZip, setCardZip] = useState('');
  // Gift cards staged toward this order. Each: { number, lastfour, balance }.
  // Balance is checked (read-only) when added; cards are NOT charged until
  // Place Order, so abandoning checkout never deducts a balance.
  const [appliedGiftCards, setAppliedGiftCards] = useState([]);
  const [giftCardNumber, setGiftCardNumber] = useState('');
  const [giftCardChecking, setGiftCardChecking] = useState(false);
  const [giftCardError, setGiftCardError] = useState('');
  
  // Schedule state. When the venue is closed now, ASAP isn't possible, so start
  // in "scheduled" mode.
  const [scheduleType, setScheduleType] = useState(venueOpen ? 'asap' : 'scheduled'); // asap, scheduled
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Contact name — ALWAYS required for the order, regardless of payment method
  // (card, gift card, Apple/Google Pay) or guest checkout. Prefilled from the
  // customer record and editable here so it can never be blank at submit.
  const [firstName, setFirstName] = useState((customer?.first_name || '').trim());
  const [lastName, setLastName] = useState((customer?.last_name || '').trim());
  useEffect(() => {
    setFirstName((customer?.first_name || '').trim());
    setLastName((customer?.last_name || '').trim());
  }, [customer]);
  
  // Delivery Address state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    addr_1: '',
    addr_2: '',
    gatecode: '',
    city: '',
    state: '',
    zip: '',
    nickname: ''
  });
  const [saveAddressToProfile, setSaveAddressToProfile] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Wallet (Apple Pay / Google Pay) state
  const [walletCfg, setWalletCfg] = useState(null);
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);

  // Detect wallet availability: device/browser capability + per-venue config.
  useEffect(() => {
    let cancelled = false;
    setApplePayReady(isApplePayAvailable()); // device capability (refined once config loads)
    if (!venueid) return;
    (async () => {
      try {
        const cfg = await api.getWalletConfig(venueid);
        if (cancelled || !cfg || cfg.result !== 1) return;
        setWalletCfg(cfg);
        setApplePayReady(isApplePayAvailable() && !!cfg.applePayEnabled);
        const gpOk = await isGooglePayAvailable(cfg);
        if (!cancelled) setGooglePayReady(gpOk);
      } catch (e) {
        console.error('Wallet config load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [venueid]);

  // Fetch saved addresses when customer changes or delivery is selected
  useEffect(() => {
    if (customer?.customerID && orderType?._onlineType === 'delivery') {
      fetchSavedAddresses();
    }
  }, [customer?.customerID, orderType?._onlineType]);

  const fetchSavedAddresses = async () => {
    if (!customer?.customerID) return;
    
    setLoadingAddresses(true);
    try {
      const response = await api.getCustomerAddresses(customer.customerID);
      if (response.result === 1 && response.addresses) {
        setSavedAddresses(response.addresses);
        // Auto-select preferred address if exists
        const preferred = response.addresses.find(a => a.preferred === '1' || a.preferred === 1);
        if (preferred) {
          setSelectedAddressId(preferred.recid);
          selectAddress(preferred);
        } else if (response.addresses.length > 0) {
          // Select first address if no preferred
          setSelectedAddressId(response.addresses[0].recid);
          selectAddress(response.addresses[0]);
        } else {
          // No saved addresses - show form
          setShowNewAddressForm(true);
        }
      } else {
        setShowNewAddressForm(true);
      }
    } catch (err) {
      console.error('Failed to load addresses:', err);
      setShowNewAddressForm(true);
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Select an address and update the delivery address state
  const selectAddress = (addr) => {
    setSelectedAddressId(addr.recid);
    setShowNewAddressForm(false);
    setAddressForm({
      addr_1: addr.addr_1 || '',
      addr_2: addr.addr_2 || '',
      gatecode: addr.gatecode || addr.addr_3 || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || '',
      nickname: addr.nickname || ''
    });
  };

  // Get the current delivery address for order submission
  const getDeliveryAddressForOrder = () => {
    if (selectedAddressId && !showNewAddressForm) {
      const addr = savedAddresses.find(a => a.recid === selectedAddressId);
      if (addr) {
        return {
          dropoff_addr: addr.addr_1 || '',
          dropoff_addr2: addr.addr_2 || '',
          dropoff_gatecode: addr.gatecode || addr.addr_3 || '',
          dropoff_city: addr.city || '',
          dropoff_state: addr.state || '',
          dropoff_zip: addr.zip || '',
          dropoff_lat: addr.lat || '',
          dropoff_lng: addr.lng || ''
        };
      }
    }
    return {
      dropoff_addr: addressForm.addr_1,
      dropoff_addr2: addressForm.addr_2,
      dropoff_gatecode: addressForm.gatecode,
      dropoff_city: addressForm.city,
      dropoff_state: addressForm.state,
      dropoff_zip: addressForm.zip,
      dropoff_lat: '',
      dropoff_lng: ''
    };
  };

  // Save new address to customer profile
  const saveNewAddress = async () => {
    if (!customer?.customerID || !saveAddressToProfile) return null;
    
    try {
      const response = await api.saveCustomerAddress({
        userid: customer.customerID,
        customerID: customer.customerID,
        addr_1: addressForm.addr_1,
        addr_2: addressForm.addr_2,
        gatecode: addressForm.gatecode,
        city: addressForm.city,
        state: addressForm.state,
        zip: addressForm.zip,
        nickname: addressForm.nickname || 'Home',
        address_type: 'delivery',
        status: '1'
      });
      return response;
    } catch (err) {
      console.error('Failed to save address:', err);
      return null;
    }
  };

  // Tip presets
  const tipPresets = [15, 18, 20, 25];

  // Calculate tip amount (rounded to the cent so it can't introduce a penny of drift)
  const tipAmount = useMemo(() => {
    const raw = tipType === 'custom' ? (parseFloat(customTip) || 0) : (cartTotals.subtotal * tip) / 100;
    return Math.round(raw * 100) / 100;
  }, [tip, tipType, customTip, cartTotals.subtotal]);

  // Calculate final total
  // Loyalty: the redemption + subscription discount reduce the SUBTOTAL, so tax
  // is recomputed on the discounted amount.
  const loyaltyDiscount = useMemo(() => {
    const redeem = loyaltyRedemption ? (Number(loyaltyRedemption.discount) || 0) : 0;
    const subPct = loyaltySub ? (Number(loyaltySub.discount_percent) || 0) : 0;
    const subDisc = subPct > 0 ? (cartTotals.subtotal * subPct) / 100 : 0;
    return Math.min(cartTotals.subtotal, Math.round((redeem + subDisc) * 100) / 100);
  }, [loyaltyRedemption, loyaltySub, cartTotals.subtotal]);

  // Tax on (subtotal − loyalty discount). Matches existing behavior when there's
  // no loyalty discount (effectiveTax === cartTotals.tax).
  const effectiveTax = useMemo(() => {
    const taxable = Math.max(0, cartTotals.subtotal - loyaltyDiscount);
    return Math.round(taxable * (cartTotals.taxRate || 0) * 100) / 100;
  }, [cartTotals.subtotal, cartTotals.taxRate, loyaltyDiscount]);

  const finalTotal = useMemo(() => {
    const promo = cartTotals.discount || 0;
    const total = cartTotals.subtotal - loyaltyDiscount - promo + effectiveTax + tipAmount;
    return Math.max(0, Math.round(total * 100) / 100);
  }, [cartTotals.subtotal, cartTotals.discount, loyaltyDiscount, effectiveTax, tipAmount]);

  // Total credit available from staged gift cards.
  const giftCardCredit = useMemo(() => {
    return appliedGiftCards.reduce((sum, gc) => sum + (parseFloat(gc.balance) || 0), 0);
  }, [appliedGiftCards]);

  // Amount of the order gift cards will actually cover (capped at the total).
  const giftCardApplied = useMemo(() => {
    return Math.min(giftCardCredit, finalTotal);
  }, [giftCardCredit, finalTotal]);

  // What still needs a card/wallet tender after gift cards. Rounded to cents to
  // avoid float dust like 0.0000001 keeping the order open.
  const remainingBalance = useMemo(() => {
    return Math.max(0, Math.round((finalTotal - giftCardApplied) * 100) / 100);
  }, [finalTotal, giftCardApplied]);

  // True when staged gift cards cover the entire order (no card/wallet needed).
  const giftCardsCoverFull = remainingBalance <= 0;

  // Generate available time slots
  // Prep-time buffer (minutes) before the earliest bookable slot today.
  const prepMinutes = Math.max(0, parseInt(settings?.prepTime, 10) || 0);

  // Available dates for scheduling: only days the venue actually opens, in the
  // venue's timezone. Skips fully-closed days. Looks ahead up to 14 days.
  const availableDates = useMemo(() => {
    const hours = activeSchedule(settings);
    const tz = settings?.hoursConfig?.timezone || venueData?.timezone;
    const out = [];
    const ymdFmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    const lblFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' });
    const todayYMD = venueNowYMDHHMM(tz).ymd;

    for (let i = 0; i < 14 && out.length < 10; i++) {
      const inst = new Date(Date.now() + i * 86400000);
      const ymd = ymdFmt.format(inst); // venue-local calendar date
      // Skip closed days when we know the schedule.
      if (hours) {
        const dow = dowForYMD(ymd);
        if (dow < 0 || schedDayWindows(hours[SCHED_DAY_NAMES[dow]]).length === 0) continue;
      }
      const label = ymd === todayYMD ? 'Today'
        : (out.length === 0 && i === 1 ? 'Tomorrow' : lblFmt.format(inst));
      out.push({ value: ymd, label });
    }
    return out;
  }, [settings, venueData]);

  // Time slots for the selected date, constrained to that day's open window(s),
  // in 15-minute increments. On the current day, past times (plus the prep-time
  // buffer) are skipped. Falls back to a generic 10:00–22:00 range when no
  // schedule is available.
  const timeSlots = useMemo(() => {
    if (!selectedDate) return [];
    const hours = activeSchedule(settings);
    const tz = settings?.hoursConfig?.timezone || venueData?.timezone;
    const now = venueNowYMDHHMM(tz);
    const isToday = selectedDate === now.ymd;
    const earliest = isToday ? hhmmToMin(now.hhmm) + prepMinutes : -1;

    // Build the list of [openMin, closeMin) ranges for the day.
    let ranges = [];
    if (hours) {
      const dow = dowForYMD(selectedDate);
      const windows = dow >= 0 ? schedDayWindows(hours[SCHED_DAY_NAMES[dow]]) : [];
      ranges = windows.map(w => [hhmmToMin(w.open), hhmmToMin(w.close)]);
    } else {
      ranges = [[10 * 60, 22 * 60]]; // fallback
    }

    const slots = [];
    ranges.forEach(([startMin, endMin]) => {
      // Round the start up to the next 15-min boundary.
      let t = Math.ceil(startMin / 15) * 15;
      for (; t < endMin; t += 15) {
        if (t <= earliest) continue; // skip past / within prep buffer today
        const hhmm = minToHHMM(t);
        const value = `${String(Math.floor(hhmm / 100)).padStart(2, '0')}:${String(hhmm % 100).padStart(2, '0')}`;
        slots.push({ value, label: schedFormatHHMM(hhmm) });
      }
    });
    return slots;
  }, [selectedDate, settings, venueData, prepMinutes]);

  // When scheduling, make sure a date is selected so the time list can populate.
  useEffect(() => {
    if (scheduleType === 'scheduled' && !selectedDate && availableDates.length) {
      setSelectedDate(availableDates[0].value);
    }
  }, [scheduleType, selectedDate, availableDates]);

  // If the chosen time is no longer valid for the chosen date, clear it.
  useEffect(() => {
    if (selectedTime && !timeSlots.some(s => s.value === selectedTime)) {
      setSelectedTime('');
    }
  }, [timeSlots, selectedTime]);

  // Format card number
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ').substr(0, 19) : '';
  };

  // Format expiry
  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substr(0, 2) + '/' + cleaned.substr(2, 2);
    }
    return cleaned;
  };

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    
    setPromoError('');
    setPromoSuccess('');
    
    const result = await onApplyPromo(promoInput.trim());
    if (result.success) {
      setPromoSuccess(result.message);
    } else {
      setPromoError(result.message);
    }
  };

  // Add a gift card to the order: check its balance, then stage it (no charge yet).
  const handleAddGiftCard = async () => {
    const number = giftCardNumber.trim();
    setGiftCardError('');
    if (!number) return;

    // Prevent staging the same card twice.
    if (appliedGiftCards.some(gc => gc.number === number)) {
      setGiftCardError('That gift card has already been added');
      return;
    }

    setGiftCardChecking(true);
    try {
      const response = await api.checkGiftCardBalance(venueid, number);
      if (response.result === 1) {
        const balance = parseFloat(response.balance) || 0;
        if (balance <= 0) {
          setGiftCardError('This gift card has a $0.00 balance');
          return;
        }
        setAppliedGiftCards(prev => [
          ...prev,
          { number, lastfour: number.replace(/\D/g, '').slice(-4), balance }
        ]);
        setGiftCardNumber('');
      } else {
        setGiftCardError(response.message || 'Invalid gift card');
      }
    } catch (err) {
      setGiftCardError('Failed to check gift card');
    } finally {
      setGiftCardChecking(false);
    }
  };

  const handleRemoveGiftCard = (number) => {
    setAppliedGiftCards(prev => prev.filter(gc => gc.number !== number));
  };

  // Process payment and submit order
  const handleSubmitOrder = async () => {
    setLoading(true);
    setError('');

    try {
      // First & last name are always required — for every payment method
      // (card, gift card, Apple/Google Pay) and for guest checkout.
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error('Please enter your first and last name.');
      }

      // Scheduling: a scheduled order (or any order placed while closed) needs a
      // pickup date AND time. ASAP is only valid when the venue is open now.
      if (scheduleType === 'scheduled' || !venueOpen) {
        if (!selectedDate || !selectedTime) {
          throw new Error('Please choose a pickup date and time.');
        }
      }

      // Validate delivery address if delivery order
      if (orderType?._onlineType === 'delivery') {
        if (showNewAddressForm || savedAddresses.length === 0) {
          // Validate new address form
          if (!addressForm.addr_1.trim()) {
            throw new Error('Please enter a street address');
          }
          if (!addressForm.city.trim()) {
            throw new Error('Please enter a city');
          }
          if (!addressForm.state.trim()) {
            throw new Error('Please enter a state');
          }
          if (!addressForm.zip.trim()) {
            throw new Error('Please enter a ZIP code');
          }
        } else if (!selectedAddressId) {
          throw new Error('Please select a delivery address');
        }
      }

      // Wallet methods (Apple Pay / Google Pay) must open their payment sheet
      // synchronously from this click, BEFORE the gift-card network calls below
      // consume the user gesture. So authorize the post-gift-card balance now;
      // the resulting tender is slotted in as the final tender further down.
      let walletTender = null;
      if ((paymentMethod === 'applepay' || paymentMethod === 'googlepay') && remainingBalance > 0) {
        const walletIsApple = paymentMethod === 'applepay';
        const walletAmount = remainingBalance;
        const walletResult = walletIsApple
          ? await startApplePay({
              venueid,
              amount: walletAmount,
              merchantDisplayName: walletCfg?.merchantDisplayName || venueData?.venuename || 'Online Order',
            })
          : await startGooglePay({ venueid, amount: walletAmount, cfg: walletCfg || {} });
        if (!walletResult) {            // user dismissed the sheet — abort quietly
          return;
        }
        walletTender = {
          // Wallet charges settle as CREDIT; walletType records which wallet.
          transtype: 'CREDIT',
          walletType: walletIsApple ? 'APPLE_PAY' : 'GOOGLE_PAY',
          // Real card network (V/M/A/D) from the wallet/processor, not 'AP'/'GP'.
          cardtype: cardCodeFromNetwork(walletResult.cardNetwork || walletResult.cardtype),
          amount: walletAmount,
          lastfour: walletResult.lastfour || (walletIsApple ? 'APLY' : 'GPAY'),
          transactionid: walletResult.transactionid || '',
          authid: walletResult.authid || walletResult.authcode || ''
        };
      }

      // -------------------------------------------------------------------
      // Process payment as one or more tenders:
      //   1) Charge each staged gift card first (partial coverage allowed).
      //   2) Charge whatever remains to the selected card/wallet method.
      // Gift cards are only charged here at submit time, never when staged.
      // -------------------------------------------------------------------
      const getCardType = (cardNum) => {
        const num = (cardNum || '').replace(/\s/g, '');
        if (/^4/.test(num)) return 'V';      // Visa
        if (/^5[1-5]/.test(num)) return 'M'; // Mastercard
        if (/^3[47]/.test(num)) return 'A';  // Amex
        if (/^6/.test(num)) return 'D';      // Discover
        return 'C';                           // Other/Credit
      };

      const tenders = [];
      let remaining = Math.round(finalTotal * 100) / 100;

      // 1) Gift cards, in the order they were added. Each charges up to the
      //    remaining balance; the backend deducts min(balance, amount) and
      //    returns amount_charged.
      for (const gc of appliedGiftCards) {
        if (remaining <= 0) break;
        const gcResult = await api.processPayment({
          venueid,
          payment_type: 'giftcard',
          card_num: gc.number,
          amount: remaining.toFixed(2),
          orderid: 0
        });
        if (gcResult.result !== 1) {
          throw new Error(gcResult.message || `Gift card ending ${gc.lastfour} was declined`);
        }
        const charged = parseFloat(
          gcResult.amount_charged != null ? gcResult.amount_charged : gcResult.amountpaid
        ) || 0;
        if (charged <= 0) {
          throw new Error(`Gift card ending ${gc.lastfour} had no available balance`);
        }
        tenders.push({
          transtype: 'GIFT',
          cardtype: 'GC',
          amount: charged,
          lastfour: gcResult.lastfour || gc.lastfour,
          transactionid: gcResult.transactionid || '',
          authid: gcResult.authcode || gcResult.authid || ''
        });
        remaining = Math.round((remaining - charged) * 100) / 100;
      }

      // 2) Remaining balance via the selected card/wallet method.
      if (remaining > 0) {
        if (paymentMethod === 'card') {
          const cleanCard = cardNumber.replace(/\s/g, '');
          if (cleanCard.length < 15) throw new Error('Please enter a valid card number');
          if (!cardExpiry || cardExpiry.length < 5) throw new Error('Please enter a valid expiry date');
          if (!cardCvv || cardCvv.length < 3) throw new Error('Please enter a valid CVV');

          const cardResult = await api.processPayment({
            venueid,
            card_num: cleanCard,
            exp_date: cardExpiry.replace('/', ''),
            cvv: cardCvv,
            zip: cardZip,
            amount: remaining.toFixed(2)
          });
          if (cardResult.result !== 1) {
            throw new Error(cardResult.message || 'Payment declined');
          }
          tenders.push({
            transtype: 'CREDIT',
            cardtype: getCardType(cardNumber),
            amount: remaining,
            lastfour: cardResult.lastfour || cleanCard.slice(-4),
            transactionid: cardResult.transactionid || '',
            authid: cardResult.authid || cardResult.authcode || ''
          });
        } else if (paymentMethod === 'applepay' || paymentMethod === 'googlepay') {
          // The wallet was already authorized up top (inside the click gesture).
          if (!walletTender) {
            throw new Error('Wallet payment was not completed');
          }
          // The sheet charged remainingBalance; it should equal the balance left
          // after gift cards (true whenever staged balances are still current).
          if (Math.abs(walletTender.amount - remaining) > 0.005) {
            console.warn('[wallet] charged', walletTender.amount, 'but remaining after gift cards is', remaining);
          }
          tenders.push(walletTender);
        } else {
          throw new Error('Please select a payment method for the remaining balance');
        }
        remaining = 0;
      }

      if (!tenders.length) {
        throw new Error('No payment was applied to this order');
      }

      // Build order items JSON in the expected format.
      // The POS expects one object per unit (qty always 1), so an item with
      // quantity N is expanded into N identical qty-1 lines.
      const orderItems = cart.flatMap(item => {
        // Build itemOptions_json from modifiers
        const blankStyle = { imageUrl: '', textSize: '', colorType: 'no color', textColor: '', backgroundcolor: '' };

        const itemOptions = (item.modifiers || []).map(mod => ({
          id: String(mod.id || mod.optionid || ''),
          sku: mod.sku || String(mod.id || ''),
          hide: false,
          name: mod.name || mod.option_name || '',
          size: [],
          price: parseFloat(mod.price) || 0,
          status: 1,
          actions: [],
          default: !!mod.default,
          activeOn: [],
          selected: true,
          toppings: [],
          matchType: mod.matchType || 0,
          printerid: mod.printerid || '',
          dataSource: 0,
          showParent: mod.showParent === true,
          description: mod.description || '',
          displayOrder: mod.displayOrder || 0,
          styleOptions: mod.styleOptions || blankStyle,
          KitchenPrintName: mod.kitchenPrintName || mod.KitchenPrintName || ''
        }));

        // Add special instructions as CUST REQ custom modifier
        if (item.specialInstructions && item.specialInstructions.trim()) {
          itemOptions.push({
            id: 'custom',
            sku: 'custom',
            hide: false,
            name: 'CUST REQ:',
            size: [],
            price: 0,
            status: 1,
            actions: [],
            default: false,
            activeOn: [],
            selected: true,
            toppings: [],
            matchType: 0,
            printerid: '',
            dataSource: 0,
            showParent: false,
            description: item.specialInstructions.trim(),
            displayOrder: 0,
            styleOptions: blankStyle,
            KitchenPrintName: ''
          });
        }

        // One line per unit: qty is always 1, and total_price is the PER-UNIT
        // price (base + modifiers), not the line total. Expand by quantity.
        const unitCount = Math.max(1, Math.round(Number(item.quantity) || 1));
        const perUnitTotal = (Number(item.price) || 0) + (Number(item.modifierTotal) || 0);
        const baseLine = {
          // Item id must be numeric for the POS (its model parses this as a number).
          id: Number(item.itemid),
          qty: 1,
          hold: 0,
          name: item.name,
          type: 'entree',
          printed: 0,
          completed: 0,
          guestIndex: 1,
          total_price: String(perUnitTotal.toFixed(2)),
          origin_price: String((Number(item.price) || 0).toFixed(2)),
          itemOptions_json: itemOptions
        };

        // Duplicate the object once per unit; give each its own itemOptions copy.
        return Array.from({ length: unitCount }, () => ({
          ...baseLine,
          itemOptions_json: itemOptions.map(o => ({ ...o }))
        }));
      });

      // Record the loyalty discount as a POS-style discount line in
      // orderItems_json (type 'discount', negative price) — same shape the POS
      // uses for any other discount. discountTaxAmount is the tax portion the
      // discount removed (we tax the discounted subtotal).
      if (loyaltyDiscount > 0) {
        let discName = 'Rewards discount';
        const redLabel = loyaltyRedemption && loyaltyRedemption.label;
        const subName = loyaltySub && (loyaltySub.plan_name || 'Membership');
        if (redLabel && subName) discName = 'Rewards: ' + redLabel + ' + ' + subName;
        else if (redLabel) discName = 'Rewards: ' + redLabel;
        else if (subName) discName = subName + ' discount';
        orderItems.push({
          id: 0, qty: 1, hold: 0, name: discName, type: 'discount',
          printed: 1, completed: 0, guestIndex: 1, manual_item: null,
          notes: '', show_on_quote: true,
          total_price: (-loyaltyDiscount).toFixed(2),
          origin_price: (-loyaltyDiscount).toFixed(2),
          discountTaxAmount: (loyaltyDiscount * (cartTotals.taxRate || 0)).toFixed(2)
        });
      }

      // Calculate pickup datetime.
      // pickupLocal = the venue wall-clock string the customer chose (for display);
      // pickupDatetime = the same instant as UTC, which is what the backend stores
      // and the POS converts back to venue-local. Storing local here is the bug
      // that shifted scheduled times by the venue's UTC offset.
      let pickupLocal = null;
      let pickupDatetime = null;
      if (scheduleType === 'scheduled' && selectedDate && selectedTime) {
        pickupLocal = `${selectedDate}T${selectedTime}:00`;
        const utcDate = venueLocalToUTC(pickupLocal, venueData?.timezone);
        pickupDatetime = utcDate ? toUTCStamp(utcDate) : pickupLocal;
      }

      // Build payment_json from the tenders processed above. The full tip is
      // attributed to the final tender (the card/wallet one, or the last gift
      // card if gift cards covered the whole order) so per-tender tipamount
      // sums to the order tip exactly once.
      //
      // amountpaid is the BASE (pre-tip) portion of what was charged on the
      // tender — NOT the tip-inclusive charge. The card/wallet is charged
      // amountpaid + tipamount; on the tip-bearing tender those two must sum to
      // the amount actually run. Computed in integer cents so the sum is exact.
      const lastTenderIdx = tenders.length - 1;
      const orderTipCents = Math.round(tipAmount * 100);
      const paymentJson = tenders.map((t, i) => {
        const chargedCents = Math.round(t.amount * 100);
        // Tip recorded on this tender (whole tip on the last one). Capped at the
        // charged amount so amountpaid can never go negative in the unlikely
        // split-tender case where the last tender is smaller than the tip.
        const tenderTipCents = (i === lastTenderIdx) ? Math.min(orderTipCents, chargedCents) : 0;
        const baseCents = chargedCents - tenderTipCents;
        return {
          tipid: t.transactionid || '',
          authid: t.authid || '',
          status: 1,
          userid: '0',        // Online order - no staff user
          orderid: 0,         // Will be set by server
          cardtype: t.cardtype,
          lastfour: t.lastfour || '',
          tipamount: (tenderTipCents / 100).toFixed(2),
          transtype: t.transtype,
          ...(t.walletType ? { walletType: t.walletType } : {}),
          amountpaid: (baseCents / 100).toFixed(2),
          terminalid: 0,
          terminalname: 'online',
          transactionid: t.transactionid || ''
        };
      });

      // Get delivery address fields if delivery order
      let deliveryFields = {};
      if (orderType?._onlineType === 'delivery') {
        deliveryFields = getDeliveryAddressForOrder();
        
        // Save new address to customer profile if requested
        if (showNewAddressForm && saveAddressToProfile && customer?.customerID) {
          await saveNewAddress();
        }
      }

      // Submit order
      const orderResult = await api.submitOrder({
        venueid,
        customerID: customer?.customerID || 0,
        buyer_first_name: firstName.trim(),
        buyer_last_name: lastName.trim(),
        buyer_phone: customer?.mobile_phone || customer?.phone || '',
        buyer_email: customer?.email || '',
        // Coerce to a real number so a stray string (e.g. 'takeout') can never be
        // POSTed and silently stored as 0 in the INT order_type_id column.
        order_type_id: Number(orderType?.id ?? orderType?.ordertypeid) || 4,
        order_type_name: orderType?.name || orderType?.order_type || 'Takeout',
        // Delivery address fields
        dropoff_addr: deliveryFields.dropoff_addr || '',
        dropoff_addr2: deliveryFields.dropoff_addr2 || '',
        dropoff_gatecode: deliveryFields.dropoff_gatecode || '',
        dropoff_city: deliveryFields.dropoff_city || '',
        dropoff_state: deliveryFields.dropoff_state || '',
        dropoff_zip: deliveryFields.dropoff_zip || '',
        dropoff_lat: deliveryFields.dropoff_lat || '',
        dropoff_lng: deliveryFields.dropoff_lng || '',
        orderItems_json: JSON.stringify(orderItems),
        payment_json: JSON.stringify(paymentJson),
        subtotal: cartTotals.subtotal.toFixed(2),
        tax: effectiveTax.toFixed(2),
        tip: tipAmount.toFixed(2),
        discount: ((cartTotals.discount || 0) + loyaltyDiscount).toFixed(2),
        total: finalTotal.toFixed(2),
        pickup_datetime: pickupDatetime,
        promo_code: promoCode || '',
        notes: '',
        source: 'online'
      });

      if (orderResult.result !== 1) {
        throw new Error(orderResult.message || 'Failed to place order');
      }

      // Loyalty: commit any redemption and award points (returning customers only).
      // Non-blocking — a loyalty hiccup must never fail a paid order.
      const loyaltyCID = customer && !customer.isGuest ? (customer.customerID || customer.customerId) : null;
      if (loyaltyCID && orderResult.orderid) {
        try {
          if (loyaltyRedemption) {
            await api.loyaltyRedeemCommit({
              venueid, customerID: loyaltyCID, orderid: orderResult.orderid,
              points: loyaltyRedemption.points, reward_id: loyaltyRedemption.reward_id, surprise_id: loyaltyRedemption.surprise_id
            });
          }
          // Points are awarded server-side by awardLoyaltyPoints() in submitOnlineOrder.
        } catch (loyErr) {
          console.warn('[loyalty] post-order', loyErr && loyErr.message);
        }
      }

      // Success - call completion handler
      onOrderComplete({
        orderid: orderResult.orderid,
        order_datetime: orderResult.order_datetime,
        total: finalTotal,
        tip: tipAmount,
        orderType: orderType,
        customer: customer,
        items: cart,
        scheduledTime: pickupLocal,
        deliveryAddress: deliveryFields
      });

    } catch (err) {
      console.error('Order error:', err);
      setError(err.message || 'Failed to process order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout">
      <div className="checkout__header">
        <button className="checkout__back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Menu
        </button>
        <h1 className="checkout__title">Checkout</h1>
      </div>

      <div className="checkout__content">
        <div className="checkout__main">
          {/* Customer Info */}
          <section className="checkout__section">
            <h2 className="checkout__section-title">Contact Information</h2>
            <div className="checkout__customer">
              {customer ? (
                <div className="checkout__customer-info">
                  <div
                    className="checkout__name-fields"
                    style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}
                  >
                    <label style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>First name *</span>
                      <input
                        className="checkout__input"
                        type="text"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name"
                        required
                      />
                    </label>
                    <label style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Last name *</span>
                      <input
                        className="checkout__input"
                        type="text"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last name"
                        required
                      />
                    </label>
                  </div>
                  <p className="checkout__customer-detail">
                    {customer.mobile_phone || customer.phone}
                  </p>
                  {customer.email && (
                    <p className="checkout__customer-detail">{customer.email}</p>
                  )}
                  <button className="checkout__customer-edit" onClick={onCustomerEdit}>
                    Change
                  </button>
                </div>
              ) : (
                <button className="checkout__customer-add" onClick={onCustomerEdit}>
                  Add Contact Information
                </button>
              )}
            </div>
          </section>

          {/* Order Type */}
          <section className="checkout__section">
            <h2 className="checkout__section-title">Order Type</h2>
            <div className="checkout__order-types">
              {availableOrderTypes.map(type => (
                <button
                  key={type.id || type.ordertypeid}
                  className={`checkout__order-type ${(orderType?.id === type.id || orderType?.ordertypeid === type.ordertypeid) ? 'checkout__order-type--active' : ''}`}
                  onClick={() => onOrderTypeChange(type)}
                >
                  {type.name || type.order_type}
                </button>
              ))}
            </div>
            
            {/* Delivery Address */}
            {orderType?._onlineType === 'delivery' && (
              <div className="checkout__delivery-section">
                <h3 className="checkout__subsection-title">Delivery Address</h3>
                
                {loadingAddresses ? (
                  <div className="checkout__loading">Loading saved addresses...</div>
                ) : (
                  <>
                    {/* Saved Addresses */}
                    {savedAddresses.length > 0 && !showNewAddressForm && (
                      <div className="checkout__saved-addresses">
                        {savedAddresses.map(addr => (
                          <div 
                            key={addr.recid}
                            className={`checkout__saved-address ${selectedAddressId === addr.recid ? 'checkout__saved-address--selected' : ''}`}
                            onClick={() => selectAddress(addr)}
                          >
                            <div className="checkout__saved-address-radio">
                              <input 
                                type="radio" 
                                name="delivery-address" 
                                checked={selectedAddressId === addr.recid}
                                onChange={() => selectAddress(addr)}
                              />
                            </div>
                            <div className="checkout__saved-address-info">
                              {addr.nickname && (
                                <span className="checkout__saved-address-nickname">{addr.nickname}</span>
                              )}
                              <span className="checkout__saved-address-line">
                                {addr.addr_1}
                                {addr.addr_2 && `, ${addr.addr_2}`}
                              </span>
                              <span className="checkout__saved-address-line">
                                {addr.city}, {addr.state} {addr.zip}
                              </span>
                            </div>
                          </div>
                        ))}
                        
                        <button 
                          className="checkout__add-address-btn"
                          onClick={() => {
                            setShowNewAddressForm(true);
                            setSelectedAddressId(null);
                            setAddressForm({ addr_1: '', addr_2: '', gatecode: '', city: '', state: '', zip: '', nickname: '' });
                          }}
                        >
                          + Add New Address
                        </button>
                      </div>
                    )}

                    {/* New Address Form */}
                    {(showNewAddressForm || savedAddresses.length === 0) && (
                      <div className="checkout__address-form">
                        {savedAddresses.length > 0 && (
                          <button 
                            className="checkout__back-to-saved"
                            onClick={() => {
                              setShowNewAddressForm(false);
                              if (savedAddresses.length > 0) {
                                selectAddress(savedAddresses[0]);
                              }
                            }}
                          >
                            ← Back to saved addresses
                          </button>
                        )}
                        
                        <div className="checkout__form-group">
                          <label className="checkout__label">Street Address *</label>
                          <input
                            type="text"
                            className="checkout__input"
                            placeholder="123 Main St"
                            value={addressForm.addr_1}
                            onChange={(e) => setAddressForm(f => ({ ...f, addr_1: e.target.value }))}
                          />
                        </div>

                        <div className="checkout__form-group">
                          <label className="checkout__label">Apt, Suite, Unit (optional)</label>
                          <input
                            type="text"
                            className="checkout__input"
                            placeholder="Apt 4B"
                            value={addressForm.addr_2}
                            onChange={(e) => setAddressForm(f => ({ ...f, addr_2: e.target.value }))}
                          />
                        </div>

                        <div className="checkout__form-group">
                          <label className="checkout__label">Gate Code (optional)</label>
                          <input
                            type="text"
                            className="checkout__input"
                            placeholder="e.g. #1234"
                            value={addressForm.gatecode}
                            onChange={(e) => setAddressForm(f => ({ ...f, gatecode: e.target.value }))}
                          />
                        </div>

                        <div className="checkout__form-row checkout__form-row--3col">
                          <div className="checkout__form-group checkout__form-group--city">
                            <label className="checkout__label">City *</label>
                            <input
                              type="text"
                              className="checkout__input"
                              placeholder="City"
                              value={addressForm.city}
                              onChange={(e) => setAddressForm(f => ({ ...f, city: e.target.value }))}
                            />
                          </div>

                          <div className="checkout__form-group checkout__form-group--state">
                            <label className="checkout__label">State *</label>
                            <input
                              type="text"
                              className="checkout__input"
                              placeholder="TX"
                              maxLength={2}
                              value={addressForm.state}
                              onChange={(e) => setAddressForm(f => ({ ...f, state: e.target.value.toUpperCase() }))}
                            />
                          </div>

                          <div className="checkout__form-group checkout__form-group--zip">
                            <label className="checkout__label">ZIP *</label>
                            <input
                              type="text"
                              className="checkout__input"
                              placeholder="75001"
                              maxLength={10}
                              value={addressForm.zip}
                              onChange={(e) => setAddressForm(f => ({ ...f, zip: e.target.value }))}
                            />
                          </div>
                        </div>

                        {customer?.customerID && (
                          <div className="checkout__save-address-option">
                            <label className="checkout__checkbox-label">
                              <input
                                type="checkbox"
                                checked={saveAddressToProfile}
                                onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                              />
                              <span>Save this address for future orders</span>
                            </label>
                            
                            {saveAddressToProfile && (
                              <div className="checkout__form-group checkout__form-group--nickname">
                                <label className="checkout__label">Address Nickname (optional)</label>
                                <input
                                  type="text"
                                  className="checkout__input"
                                  placeholder="Home, Work, etc."
                                  value={addressForm.nickname}
                                  onChange={(e) => setAddressForm(f => ({ ...f, nickname: e.target.value }))}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

          {/* Schedule */}
          <section className="checkout__section">
            <h2 className="checkout__section-title">When</h2>
            <div className="checkout__schedule">
              {!venueOpen && (
                <p style={{ margin: '0 0 10px', color: '#b91c1c', fontWeight: 600, fontSize: '0.9rem' }}>
                  We're closed right now — choose a time when we're open to schedule your order.
                </p>
              )}
              <div className="checkout__schedule-options">
                <button
                  className={`checkout__schedule-btn ${scheduleType === 'asap' ? 'checkout__schedule-btn--active' : ''}`}
                  onClick={() => { if (venueOpen) setScheduleType('asap'); }}
                  disabled={!venueOpen}
                  title={!venueOpen ? 'Closed now — schedule for later' : undefined}
                  style={!venueOpen ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                >
                  ASAP
                </button>
                <button
                  className={`checkout__schedule-btn ${scheduleType === 'scheduled' ? 'checkout__schedule-btn--active' : ''}`}
                  onClick={() => setScheduleType('scheduled')}
                >
                  Schedule
                </button>
              </div>

              {scheduleType === 'scheduled' && (
                <div className="checkout__schedule-picker">
                  <select
                    className="checkout__select"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  >
                    <option value="">Select Date</option>
                    {availableDates.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                  <select
                    className="checkout__select"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                  >
                    <option value="">{timeSlots.length ? 'Select Time' : 'No times available'}</option>
                    {timeSlots.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>

          {/* Tip */}
          <section className="checkout__section">
            <h2 className="checkout__section-title">Add a Tip</h2>
            <div className="checkout__tip">
              <div className="checkout__tip-presets">
                {tipPresets.map(pct => (
                  <button
                    key={pct}
                    className={`checkout__tip-btn ${tipType === 'percent' && tip === pct ? 'checkout__tip-btn--active' : ''}`}
                    onClick={() => {
                      setTipType('percent');
                      setTip(pct);
                    }}
                  >
                    {pct}%
                    <span className="checkout__tip-amount">
                      ${((cartTotals.subtotal * pct) / 100).toFixed(2)}
                    </span>
                  </button>
                ))}
                <button
                  className={`checkout__tip-btn ${tipType === 'custom' ? 'checkout__tip-btn--active' : ''}`}
                  onClick={() => setTipType('custom')}
                >
                  Custom
                </button>
              </div>
              
              {tipType === 'custom' && (
                <div className="checkout__tip-custom">
                  <span className="checkout__tip-custom-prefix">$</span>
                  <input
                    type="number"
                    className="checkout__tip-custom-input"
                    placeholder="0.00"
                    value={customTip}
                    onChange={(e) => setCustomTip(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              )}
            </div>
          </section>

          {/* Promo Code */}
          <section className="checkout__section">
            <h2 className="checkout__section-title">Promo Code</h2>
            <div className="checkout__promo">
              <input
                type="text"
                className="checkout__promo-input"
                placeholder="Enter code"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              />
              <button 
                className="checkout__promo-btn"
                onClick={handleApplyPromo}
              >
                Apply
              </button>
            </div>
            {promoError && <p className="checkout__promo-error">{promoError}</p>}
            {promoSuccess && <p className="checkout__promo-success">{promoSuccess}</p>}
          </section>

          {/* Payment */}
          <section className="checkout__section">
            <h2 className="checkout__section-title">Payment</h2>

            {/* Gift Cards - additive; stack as many as needed, charged first */}
            {settings?.paymentSettings?.giftCard && (
              <div className="checkout__giftcard-form">
                <label className="checkout__label">Gift Card</label>

                {/* Staged gift cards */}
                {appliedGiftCards.length > 0 && (
                  <ul className="checkout__giftcard-list">
                    {appliedGiftCards.map((gc) => (
                      <li key={gc.number} className="checkout__giftcard-item">
                        <span className="checkout__giftcard-item-label">
                          Gift card •••• {gc.lastfour}
                        </span>
                        <span className="checkout__giftcard-item-balance">
                          ${gc.balance.toFixed(2)}
                        </span>
                        <button
                          type="button"
                          className="checkout__giftcard-remove"
                          onClick={() => handleRemoveGiftCard(gc.number)}
                          aria-label="Remove gift card"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add another gift card */}
                <div className="checkout__giftcard-input-row">
                  <input
                    type="text"
                    className="checkout__input"
                    placeholder="Enter gift card number"
                    value={giftCardNumber}
                    onChange={(e) => { setGiftCardNumber(e.target.value); setGiftCardError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddGiftCard(); } }}
                  />
                  <button
                    type="button"
                    className="checkout__giftcard-check"
                    onClick={handleAddGiftCard}
                    disabled={giftCardChecking || !giftCardNumber.trim()}
                  >
                    {giftCardChecking ? 'Checking…' : 'Add'}
                  </button>
                </div>
                {giftCardError && (
                  <p className="checkout__giftcard-error">{giftCardError}</p>
                )}

                {/* Coverage summary */}
                {appliedGiftCards.length > 0 && (
                  <div className="checkout__giftcard-summary">
                    <div className="checkout__giftcard-summary-row">
                      <span>Gift card credit applied</span>
                      <span>-${giftCardApplied.toFixed(2)}</span>
                    </div>
                    <div className="checkout__giftcard-summary-row">
                      <span>{giftCardsCoverFull ? 'Remaining balance' : 'Remaining balance (other payment)'}</span>
                      <span>${remainingBalance.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Remaining-balance payment method. Hidden when gift cards cover
                the whole order. */}
            {giftCardsCoverFull ? (
              <p className="checkout__payment-covered">
                Your gift card{appliedGiftCards.length > 1 ? 's' : ''} cover the full order total. No other payment needed.
              </p>
            ) : (
              <>
                {appliedGiftCards.length > 0 && (
                  <p className="checkout__payment-remainder-note">
                    Choose how to pay the remaining ${remainingBalance.toFixed(2)}:
                  </p>
                )}

                <div className="checkout__payment-methods">
                  {/* Credit Card - Always available */}
                  <button
                    className={`checkout__payment-method ${paymentMethod === 'card' ? 'checkout__payment-method--active' : ''}`}
                    onClick={() => setPaymentMethod('card')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                    Credit Card
                  </button>

                  {/* Apple Pay - only if the device/browser supports it AND the venue enabled it */}
                  {applePayReady && (
                    <button
                      className={`checkout__payment-method ${paymentMethod === 'applepay' ? 'checkout__payment-method--active' : ''}`}
                      onClick={() => setPaymentMethod('applepay')}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      Apple Pay
                    </button>
                  )}

                  {/* Google Pay - only if the device/browser supports it AND the venue enabled it */}
                  {googlePayReady && (
                    <button
                      className={`checkout__payment-method ${paymentMethod === 'googlepay' ? 'checkout__payment-method--active' : ''}`}
                      onClick={() => setPaymentMethod('googlepay')}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
                      </svg>
                      Google Pay
                    </button>
                  )}
                </div>

                {paymentMethod === 'card' && (
                  <div className="checkout__card-form">
                    <div className="checkout__form-group">
                      <label className="checkout__label">Card Number</label>
                      <input
                        type="text"
                        className="checkout__input"
                        placeholder="1234 5678 9012 3456"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        maxLength={19}
                      />
                    </div>
                    <div className="checkout__form-row">
                      <div className="checkout__form-group">
                        <label className="checkout__label">Expiry</label>
                        <input
                          type="text"
                          className="checkout__input"
                          placeholder="MM/YY"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                          maxLength={5}
                        />
                      </div>
                      <div className="checkout__form-group">
                        <label className="checkout__label">CVV</label>
                        <input
                          type="text"
                          className="checkout__input"
                          placeholder="123"
                          value={cardCvv}
                          onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').substr(0, 4))}
                          maxLength={4}
                        />
                      </div>
                      <div className="checkout__form-group">
                        <label className="checkout__label">ZIP</label>
                        <input
                          type="text"
                          className="checkout__input"
                          placeholder="12345"
                          value={cardZip}
                          onChange={(e) => setCardZip(e.target.value.replace(/\D/g, '').substr(0, 5))}
                          maxLength={5}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'applepay' && (
                  <div className="checkout__wallet-form">
                    <p className="checkout__wallet-info">
                      Tap "Place Order" to pay with Apple Pay. The Apple Pay sheet will open to confirm.
                    </p>
                    <div className="checkout__wallet-button checkout__wallet-button--apple">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      Pay with Apple Pay
                    </div>
                  </div>
                )}

                {paymentMethod === 'googlepay' && (
                  <div className="checkout__wallet-form">
                    <p className="checkout__wallet-info">
                      Tap "Place Order" to pay with Google Pay. The Google Pay sheet will open to confirm.
                    </p>
                    <div className="checkout__wallet-button checkout__wallet-button--google">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
                      </svg>
                      Pay with Google Pay
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {error && (
            <div className="checkout__error">
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Order Summary Sidebar */}
        <div className="checkout__sidebar">
          <div className="checkout__summary">
            <h3 className="checkout__summary-title">Order Summary</h3>

            <LoyaltyPanel
              venueid={venueid}
              customer={customer}
              subtotal={cartTotals.subtotal}
              onRedemptionChange={setLoyaltyRedemption}
              onSubscriptionChange={setLoyaltySub}
            />
            
            <div className="checkout__summary-items">
              {cart.map(item => (
                <div key={item.id} className="checkout__summary-item">
                  <span className="checkout__summary-item-qty">{item.quantity}×</span>
                  <span className="checkout__summary-item-name">{item.name}</span>
                  <span className="checkout__summary-item-price">${item.lineTotal.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="checkout__summary-totals">
              <div className="checkout__summary-row">
                <span>Subtotal</span>
                <span>${cartTotals.subtotal.toFixed(2)}</span>
              </div>
              {loyaltyDiscount > 0 && (
                <div className="checkout__summary-row checkout__summary-row--discount">
                  <span>Rewards discount</span>
                  <span>-${loyaltyDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="checkout__summary-row">
                <span>Tax</span>
                <span>${effectiveTax.toFixed(2)}</span>
              </div>
              {cartTotals.discount > 0 && (
                <div className="checkout__summary-row checkout__summary-row--discount">
                  <span>Discount</span>
                  <span>-${cartTotals.discount.toFixed(2)}</span>
                </div>
              )}
              {tipAmount > 0 && (
                <div className="checkout__summary-row">
                  <span>Tip</span>
                  <span>${tipAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="checkout__summary-row checkout__summary-row--total">
                <span>Total</span>
                <span>${finalTotal.toFixed(2)}</span>
              </div>
            </div>

            <button
              className="checkout__submit-btn"
              onClick={handleSubmitOrder}
              disabled={loading || !customer}
            >
              {loading ? (
                <span className="checkout__submit-loading">Processing...</span>
              ) : (
                `Place Order • $${finalTotal.toFixed(2)}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;